// Client-side TIFF → displayable JPEG preview.
//
// Why this exists: orthomosaics are often 1-2 GB TIFFs. Uploading them to the
// server to generate a display thumbnail was taking minutes (upload bandwidth)
// and crashing the gunicorn worker (Pillow decodes the full raster in RAM).
// geotiff.js can read a decimated version of the raster directly in the
// browser using tile-strided reads, so the full file never leaves the user's
// machine for display purposes.
//
// Geo metadata is still extracted from the TIFF by GeoInfoReader.js; this
// module only handles the display path. The two could share a decoded tiff
// instance as an optimization, but separation keeps GeoInfoReader untouched.

import { fromBlob } from 'geotiff';

const DEFAULT_MAX_DIMENSION = 4096;
const JPEG_QUALITY = 0.85;

// Progress stages. We report normalized progress (0..1) alongside a label so
// the UI can show both a bar and a human-readable current step.
const STAGES = {
  OPEN: 'Opening TIFF…',
  READ: 'Reading pixels…',
  RENDER: 'Building preview…',
  ENCODE: 'Encoding JPEG…',
};

/**
 * Build a displayable JPEG preview from a TIFF file, entirely client-side.
 *
 * @param {File|Blob} file - the TIFF file from the <input>
 * @param {object} [opts]
 * @param {number} [opts.maxDimension=4096] - longest edge of output preview
 * @param {(progress: number, stage: string) => void} [opts.onProgress]
 * @returns {Promise<{ objectUrl: string, origW: number, origH: number }>}
 */
export async function buildPreviewFromTiff(file, opts = {}) {
  const maxDimension = opts.maxDimension || DEFAULT_MAX_DIMENSION;
  const onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : () => {};

  onProgress(0.02, STAGES.OPEN);
  const tiff = await fromBlob(file);

  // Image 0 in a TIFF is the full-resolution raster. For orthomosaics the
  // encoder (Agisoft, Pix4D, ODM, GDAL) typically writes additional images
  // that are pre-downsampled "overviews" — 1/2, 1/4, 1/8 resolution, etc.
  // Reading from a small overview is dramatically cheaper than decimating
  // the full resolution on the fly: geotiff.js has to allocate enough
  // memory to hold an entire intermediate tile/strip even when the *output*
  // is small, and on 1+ GB TIFFs with large strip layouts that allocation
  // can fail (RangeError: Array buffer allocation failed).
  //
  // Strategy: pick the smallest overview whose longest edge is still >=
  // maxDimension. If no overview is large enough (small TIFF or no
  // overviews present), fall back to image 0 and let readRasters decimate.
  const imageCount = await tiff.getImageCount();
  const fullImage = await tiff.getImage(0);
  const origW = fullImage.getWidth();
  const origH = fullImage.getHeight();
  const samplesPerPixel = fullImage.getSamplesPerPixel();

  let sourceImage = fullImage;
  let sourceIndex = 0;
  for (let i = 1; i < imageCount; i++) {
    const candidate = await tiff.getImage(i);
    const cw = candidate.getWidth();
    const ch = candidate.getHeight();
    // Only consider overviews (smaller than full res); skip anything that
    // looks equal-or-larger (rare, but some files embed sibling images
    // that aren't true pyramid levels).
    if (cw >= origW || ch >= origH) continue;
    const longEdge = Math.max(cw, ch);
    if (longEdge >= maxDimension) {
      // Prefer the smallest overview still big enough to meet our quality
      // target — saves decode work.
      if (sourceImage === fullImage || longEdge < Math.max(sourceImage.getWidth(), sourceImage.getHeight())) {
        sourceImage = candidate;
        sourceIndex = i;
      }
    }
  }
  if (sourceIndex > 0) {
    // eslint-disable-next-line no-console
    console.info(`[tiff-preview] using overview #${sourceIndex} ${sourceImage.getWidth()}x${sourceImage.getHeight()} (full res ${origW}x${origH})`);
  }

  // Compute target size preserving aspect ratio (from ORIGINAL dims so geo
  // math downstream stays correct). Never upscale.
  const scale = Math.min(1, maxDimension / Math.max(origW, origH));
  const targetW = Math.max(1, Math.round(origW * scale));
  const targetH = Math.max(1, Math.round(origH * scale));

  onProgress(0.08, STAGES.READ);

  // readRasters with explicit width/height does a strided/decimated read
  // from whichever image we selected. Using an overview here means the
  // intermediate tile buffer is O(overview size), not O(full TIFF size).
  //
  // Even with overviews we can still hit "Array buffer allocation failed"
  // on TIFFs with large strip rows or no overview pyramid. Retry with
  // progressively smaller targets before giving up — a 1024-px preview is
  // still better than the server fallback's multi-minute wait.
  //
  // `interleave: true` returns [R,G,B,R,G,B,...] which matches ImageData
  // layout and avoids an extra copy/reshuffle.
  let rasters = null;
  let actualTargetW = targetW;
  let actualTargetH = targetH;
  const attemptSizes = [
    { w: targetW, h: targetH },
    { w: Math.max(1, Math.round(targetW / 2)), h: Math.max(1, Math.round(targetH / 2)) },
    { w: Math.max(1, Math.round(targetW / 4)), h: Math.max(1, Math.round(targetH / 4)) },
  ];
  let lastErr = null;
  for (const { w, h } of attemptSizes) {
    try {
      // eslint-disable-next-line no-await-in-loop
      rasters = await sourceImage.readRasters({
        width: w,
        height: h,
        interleave: true,
        fillValue: 0,
      });
      actualTargetW = w;
      actualTargetH = h;
      if (w !== targetW) {
        // eslint-disable-next-line no-console
        console.warn(`[tiff-preview] readRasters failed at ${targetW}x${targetH}, succeeded at ${w}x${h}`);
      }
      break;
    } catch (e) {
      lastErr = e;
      // Only retry on allocation failures — other errors are usually
      // genuine format problems the server can't fix either.
      if (!(e instanceof RangeError)) throw e;
    }
  }
  if (!rasters) {
    throw lastErr || new Error('readRasters failed at all attempted sizes');
  }

  onProgress(0.78, STAGES.RENDER);

  // readRasters returns a TypedArray (Uint8Array/Uint16Array/Float32Array
  // depending on the source bit depth). We need Uint8ClampedArray in RGBA
  // order for ImageData. Handle the three common cases:
  //   - 1 sample  (grayscale): replicate to RGB, alpha=255
  //   - 3 samples (RGB):       add alpha=255
  //   - 4 samples (RGBA):      use directly if already 8-bit
  // For non-8-bit source data, do a naive normalization to 0..255. This is
  // a display path, not a scientific path; accuracy of the preview isn't
  // critical.
  const rgba = typedArrayToRGBA(rasters, actualTargetW, actualTargetH, samplesPerPixel);

  // Draw to a canvas, then toBlob to get a compressed JPEG.
  const canvas = document.createElement('canvas');
  canvas.width = actualTargetW;
  canvas.height = actualTargetH;
  const ctx = canvas.getContext('2d');
  const imageData = new ImageData(rgba, actualTargetW, actualTargetH);
  ctx.putImageData(imageData, 0, 0);

  onProgress(0.92, STAGES.ENCODE);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      'image/jpeg',
      JPEG_QUALITY
    );
  });

  const objectUrl = URL.createObjectURL(blob);
  onProgress(1.0, STAGES.ENCODE);

  return { objectUrl, origW, origH };
}

// Convert whatever geotiff.js returned into Uint8ClampedArray[RGBA].
// Handles 8-bit and 16-bit integer rasters plus 1/3/4 channel layouts.
// Defensive: unexpected shapes fall back to grayscale + alpha=255 so we
// never throw from here; the canvas will still render *something*.
function typedArrayToRGBA(src, width, height, samplesPerPixel) {
  const pxCount = width * height;
  const dst = new Uint8ClampedArray(pxCount * 4);

  // Detect bit depth heuristically. geotiff.js interleaved typed-array
  // options include Uint8Array, Uint16Array, Int16Array, Float32Array. For
  // 16-bit we shift down; for float we clamp to 0..1 → 0..255.
  let normalizeToByte;
  if (src instanceof Uint8Array || src instanceof Uint8ClampedArray) {
    normalizeToByte = (v) => v;
  } else if (src instanceof Uint16Array || src instanceof Int16Array) {
    normalizeToByte = (v) => v >> 8; // fast 16→8 (drop low byte)
  } else if (src instanceof Float32Array || src instanceof Float64Array) {
    // Assume already normalized 0..1; if not, clamp. If sources routinely
    // come in as 0..65535 floats we'd need smarter auto-ranging, but
    // orthomosaic TIFFs we've seen are 8-bit RGB so this branch is rare.
    normalizeToByte = (v) => {
      if (v <= 1) return Math.max(0, Math.min(255, Math.round(v * 255)));
      return Math.max(0, Math.min(255, Math.round(v)));
    };
  } else {
    normalizeToByte = (v) => v;
  }

  if (samplesPerPixel === 1) {
    for (let i = 0; i < pxCount; i++) {
      const g = normalizeToByte(src[i]);
      const j = i * 4;
      dst[j] = g;
      dst[j + 1] = g;
      dst[j + 2] = g;
      dst[j + 3] = 255;
    }
  } else if (samplesPerPixel === 3) {
    for (let i = 0; i < pxCount; i++) {
      const si = i * 3;
      const di = i * 4;
      dst[di] = normalizeToByte(src[si]);
      dst[di + 1] = normalizeToByte(src[si + 1]);
      dst[di + 2] = normalizeToByte(src[si + 2]);
      dst[di + 3] = 255;
    }
  } else if (samplesPerPixel >= 4) {
    // Use first 4 channels; extra bands (e.g. NIR) are ignored for display.
    for (let i = 0; i < pxCount; i++) {
      const si = i * samplesPerPixel;
      const di = i * 4;
      dst[di] = normalizeToByte(src[si]);
      dst[di + 1] = normalizeToByte(src[si + 1]);
      dst[di + 2] = normalizeToByte(src[si + 2]);
      dst[di + 3] = normalizeToByte(src[si + 3]);
    }
  } else {
    // samplesPerPixel === 0 or other weirdness. Fill black.
    dst.fill(0);
    for (let i = 3; i < dst.length; i += 4) dst[i] = 255;
  }

  return dst;
}
